import React, { forwardRef } from "react";
import Box, { BoxProps } from "../box/Box";
import { useHasImageLoaded } from "../image/Image";
import { useTheme } from "../theme/ThemeProvider";
import DefaultAvatar from "./DefaultAvatar";
import DisruptorIcon from "./DisruptorIcon";
import { getSecureContent } from "../anchor";

/**
 * sm = 32px
 * md = 48px
 */
export type Size =
  /**
   * The small size
   */
  | "2xs"
  /**
   * Not so small
   */
  | "xs"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl";

export interface IAvatar {
  /**
   * The name of the person in the avatar.
   *
   * - if `src` has loaded, the name will be used as the `alt` attribute of the `img`
   * - If `src` is not loaded, the name will be used to create the initials
   */
  name?: string | null;
  /**
   * The size of the avatar.
   */
  size?: Size | null;
  /**
   * If `true`, the `Avatar` will show a border around it.
   *
   * Best for a group of avatars
   */
  showBorder?: boolean;
  /**
   * The badge at the bottom right corner of the avatar.
   */
  children?: React.ReactNode;
  /**
   * The image url of the `Avatar`
   */
  src?: string | null;

  variant?: "disruptor" | "disruptor-without-icon" | null;

  external?: "outside-govlaunch" | "govlaunch" | "outside-redirect";
}

export type AvatarProps = IAvatar & Omit<BoxProps, "size">;

const Img = Box.withComponent("img");

const Avatar = forwardRef<any, AvatarProps>(
  (
    {
      size = "md",
      showBorder = true,
      border = "1px",
      borderColor = "gray.200",
      name,
      src,
      external,
      children,
      variant,
      ...rest
    },
    ref,
  ) => {
    const avatarStyleProps = useAvatarStyle({
      name,
      size,
      showBorder,
      border,
      borderColor,
    });
    const imageSrc = src ? getSecureContent(src, external) : undefined;
    const hasLoaded = useHasImageLoaded({ src: imageSrc });

    const theme = useTheme();
    const sizeKey = size ? avatarSizes[size] : "md";
    // @ts-ignore
    const _size = theme.sizes[sizeKey];
    const fontSize = parseFloat(`calc(${_size} / 2.5)`);

    const renderChildren = () => {
      if (src && hasLoaded) {
        return (
          <Img
            as="img"
            size="100%"
            rounded="full"
            objectFit="cover"
            src={imageSrc}
            alt={name || undefined}
            bg="white"
          />
        );
      }

      if (src && !hasLoaded) {
        if (name) {
          return <AvatarName size={_size} name={name} />;
        }
        return <DefaultAvatar aria-label={name || undefined} />;
      }

      if (!src && name) {
        return <AvatarName size={_size} name={name} />;
      }

      return <DefaultAvatar aria-label={name || undefined} />;
    };

    let variantProps: BoxProps = {};
    const variantIconSizeInPixels = parseFloat(_size) * 4;

    if (variant === "disruptor" || variant === "disruptor-without-icon") {
      variantProps = {
        border: variantIconSizeInPixels < 16 ? "2px" : "4px",
        borderColor: "orange.700",
      };
    }

    return (
      <Box
        ref={ref}
        fontSize={fontSize}
        lineHeight={_size}
        verticalAlign="top"
        {...avatarStyleProps}
        {...variantProps}
        {...rest}
      >
        {renderChildren()}
        {children}

        {variant === "disruptor" && (
          <Box position="absolute" bottom={0} right={0}>
            <DisruptorIcon width={variantIconSizeInPixels} height={variantIconSizeInPixels} />
          </Box>
        )}
      </Box>
    );
  },
);

Avatar.defaultProps = {
  size: "md",
};

export default Avatar;

const getInitials = (name: string) => {
  const [firstName, lastName] = name.split(" ");

  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`;
  }
  return firstName.charAt(0);
};

const AvatarName = ({ name, ...props }: BoxProps & { name?: string }) => {
  return (
    <Box textAlign="center" textTransform="uppercase" fontWeight="medium" aria-label={name} {...props}>
      {name ? getInitials(name) : null}
    </Box>
  );
};

export const avatarSizes = {
  "2xs": 4,
  xs: 6,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  "2xl": 32,
  full: "full",
};

interface IAvatarStyleProps {
  size?: Size | null;
  name?: string | null;
  showBorder?: boolean;
  border?: BoxProps["border"];
  borderColor?: BoxProps["borderColor"];
}

const useAvatarStyle = ({ size = "md", border, borderColor, name, showBorder }: IAvatarStyleProps) => {
  const bg = name ? "gray.100" : "gray.400";
  const color = name ? "gray.800" : "#fff";

  const baseProps: BoxProps = {
    display: "inline-flex",
    rounded: "full",
    alignItems: "center",
    flexShrink: 0,
    justifyContent: "center",
    position: "relative",
  };

  return {
    ...baseProps,
    size: size ? avatarSizes[size] : null,
    bg,
    color,
    ...(showBorder && {
      border: border || "2px",
      borderColor,
    }),
  };
};
